import {
  GridColDef,
  GridFilterInputSingleSelect,
  GridFilterInputMultipleSingleSelect,
  GridFilterOperator,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import {
  FY_BUDGET_FILTER_FIELD,
  fyBudgetFilterOperator,
} from './toolbar/FYBudgetFilter';
import { TType } from '../../hooks/useTranslation';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import Button from '@ingka/button';
import SSRIcon from '@ingka/ssr-icon';
import Badge from '@ingka/badge';
import checkmark from '@ingka/ssr-icon/paths/checkmark';
import documentCheckmark from '@ingka/ssr-icon/paths/document-checkmark';
import document from '@ingka/ssr-icon/paths/document';
import chevron from '@ingka/ssr-icon/paths/chevron-right';
import chevronDown from '@ingka/ssr-icon/paths/chevron-down';
import {
  getBooleanValue,
  getTranslatedValue,
} from '../ApprovalPageTable/utils';
import { projectFromReltp } from '../../utils/fieldEditingRules';
import { getFY, getFyRange } from '../../utils/calculation';
import { FUTURE_FORECAST_FYS } from '../../store/utils/utils';
import { Stack } from '@mui/material';
import Tooltip from '@ingka/tooltip';
import accounting from 'accounting-js';
import { SolutionPuposesEnum } from '../../models/selectOptionsModels';

export const getFormattedDate = (value) => {
  return value ? format(new Date(value), 'yyyy-MM-dd') : '';
};

export const filterLadaProjects = (projects) => {
  return projects.filter((project) => {
    const projectNameIncludesLada = project.projectName
      ?.toLowerCase()
      .includes('lada');
    const unitTypeIsLada = [
      'ikeaSmallStoreLadaS',
      'ikeaSmallStoreLadaM',
    ].includes(project.unitType);
    return projectNameIncludesLada || unitTypeIsLada;
  });
};

export const returnNumberColumn = (
  headerName,
  field,
  width = 120,
  formattingPrecision = 0,
  symbol = (params: GridRenderCellParams) => params?.row?.currencySymbol || '',
) => ({
  field,
  type: 'number',
  headerName,
  width,
  display: 'flex',
  groupable: true,
  aggregable: true,
  valueGetter: (value) => {
    return (value === 'string' ? parseInt(value) : value) || 0;
  },
  renderCell: (params: GridRenderCellParams) => (
    <Typography noWrap alignSelf={'anchor-center'}>
      {accounting.formatMoney(params.value, {
        precision: formattingPrecision,
        symbol: symbol(params),
      })}
    </Typography>
  ),
});

export const defaultRenderer = (params: GridRenderCellParams) => {
  const { value, formattedValue } = params;
  const displayValue = value instanceof Date ? formattedValue : value;
  return <Typography noWrap>{displayValue}</Typography>;
};
export const groupColumn = (params: GridRenderCellParams) =>
  params?.value && (
    <Stack direction="row" spacing={1} alignItems="center" height={'100%'}>
      <SSRIcon
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        paths={params?.rowNode?.childrenExpanded ? chevronDown : chevron}
      />
      <Badge
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        label={`${params?.value} (${params.rowNode?.children.length})`}
        colour="grey"
        size="small"
      />
    </Stack>
  );

export const badgeRenderer = (
  params: GridRenderCellParams,
  displayValue = '',
) =>
  params?.value && (
    <Badge label={displayValue || params?.value} colour="grey" size="small" />
  );

export const textColumn = (
  field: string,
  headerName: string,
  minWidth?: number,
  grouping?: boolean,
  aggregable?: boolean,
) => {
  return {
    field,
    headerName,
    minWidth: minWidth,
    groupable: grouping,
    aggregable: aggregable,
    display: 'flex',
    renderCell: defaultRenderer,
    groupingValueGetter: (value) => value,
  };
};

export const checkmarkColumn = (
  field: string,
  headerName: string,
  minWidth?: number,
) => {
  return {
    field,
    headerName,
    display: 'flex',
    minWidth: minWidth,
    renderCell: (params: GridRenderCellParams) => {
      return params?.value ? (
        <div
          className="badge badge--green badge--icon badge--icon-only"
          style={{
            minWidth: '1.25rem',
            minHeight: '1.25rem',
            width: '1.25rem',
            height: '1.25rem',
          }}
        >
          <SSRIcon paths={checkmark} />
        </div>
      ) : null;
    },
  };
};

export const includesValueOperator: GridFilterOperator = {
  label: 'includes',
  value: 'includes',
  getApplyFilterFn: (filterItem) => {
    if (!filterItem?.value) return null;
    return (params) => {
      const cellValue = params;
      const filterValue = filterItem?.value;
      if (cellValue == null) return false;
      if (typeof cellValue === 'string') {
        return cellValue
          .split(',')
          .map((v) => v.trim())
          .includes(filterValue);
      }
      return cellValue === filterValue;
    };
  },
  InputComponent: GridFilterInputSingleSelect,
};

const isAnyOfCrpOperator: GridFilterOperator = {
  label: 'is any of',
  value: 'isAnyOf',
  getApplyFilterFn: (filterItem) => {
    if (!Array.isArray(filterItem?.value) || !filterItem.value.length)
      return null;
    return (params) => {
      if (params == null) return false;
      const cellValues =
        typeof params === 'string'
          ? params.split(',').map((v) => v.trim())
          : [params];
      return filterItem.value.some((fv) => cellValues.includes(fv));
    };
  },
  InputComponent: GridFilterInputMultipleSingleSelect,
};

export const CRP_FY_RANGE_OPTIONS = [
  'FY27 - FY29',
  'FY28 - FY30',
  'FY29 - FY31',
];

export const FINANCIAL_COLUMNS = [
  'totalBudget',
  'totalForecast',
  'totalReleased',
  'totalActuals',
  'totalLand',
  'totalProperty',
  'totalRetail',
  'totalRetailCost',
  'totalBudgetAllFY',
];

const NextFyForecast = [
  'forecastLandCost',
  'forecastPropertyInvestment',
  'forecastRetailInvestment',
  'forecastRetailCost',
];

const FUTURE_FORECAST_FIELDS = [
  'forecastLandCost',
  'forecastPropertyInvestment',
  'forecastRetailInvestment',
  'forecastRetailCost',
];

const FutureFyForecastColumns = FUTURE_FORECAST_FYS.flatMap((fy) =>
  FUTURE_FORECAST_FIELDS.map((field) => `${field}${fy}`),
);

export const returnSingleSelectColumn = (
  field,
  headerName,
  valueOptions,
  translationKey = null,
  t = null,
  badge = false,
) => ({
  field,
  headerName,
  type: 'singleSelect',
  valueOptions,
  aggregable: false,
  minWidth: 150,
  display: 'flex',
  valueGetter: translationKey
    ? (value) => getTranslatedValue(value, translationKey, t)
    : (value) => value,
  groupingValueGetter: translationKey
    ? (value) => getTranslatedValue(value, translationKey, t)
    : (value) => value,
  renderCell: badge ? badgeRenderer : defaultRenderer,
});

const getFinancialColumns = (t: TType) => {
  return FINANCIAL_COLUMNS.map((column) => {
    return returnNumberColumn(t(`ppm.columns.${column}`), column, 100);
  });
};

const getNextForecastColumns = (t: TType) => {
  return [...NextFyForecast].map((column) => {
    return returnNumberColumn(
      t(`ppm.columns.${column.split('-')[0]}`) +
        ` ${column.split('-')[1] || ''}`,
      column,
      100,
    );
  });
};

const getFutureFyForecastColumns = (t: TType) => {
  return FutureFyForecastColumns.map((column) =>
    returnNumberColumn(t(`ppm.columns.${column}`), column, 100),
  );
};
export const getOptionsForTable = (t: TType, keyName, onlyValues = false) => {
  const object = t(keyName);
  if (onlyValues) {
    return Object.values(object);
  }
  return Object.keys(object).map((key) => ({
    value: key,
    label: object[key],
  }));
};

export const getSolutionsLabelTextForGrid = (
  textValue: string,
  translationObject,
) => {
  return textValue
    .split(', ')
    .map((text: string) => (text ? translationObject[text] : ''))
    .filter((str) => str?.trim() !== '')
    .join(', ');
};

export const getUnitTypeValue = (params, t: TType) => {
  const row = params?.row;
  if (row?.establishmentType === 'FORMAT_CHANGE') {
    return getTranslatedValue(row?.newUnitType, 'unitTypes', t);
  }
  return getTranslatedValue(params?.value || params, 'unitTypes', t);
};

export const booleanSelectOptions = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

export const getBooleanColumn = (field, headerName, t) => ({
  field,
  headerName,
  minWidth: 100,
  display: 'flex',
  aggregable: false,
  type: 'singleSelect',
  valueOptions: booleanSelectOptions,
  renderCell: (params) => getBooleanValue(params.value, t),
});

import { BadgeProps } from '@ingka/badge/Badge';
import { emptyStats } from '../../store/projects/utils';
import { EUR } from '../../constants';

export const confidentialityColourMap: Record<string, BadgeProps['colour']> = {
  INTERNAL: 'grey',
  CONFIDENTIAL: 'yellow',
  STRICTLY_CONFIDENTIAL: 'red',
};

const crpBadgeRenderer = (value: string, colour: BadgeProps['colour']) =>
  value ? (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Badge label={value.split(', ')[0]} colour={colour} size="small" />
      {value.split(', ').length > 1 && (
        <Tooltip
          tooltipText={value}
          describedById={'crp-tooltip'}
          position={'bottom'}
        >
          <Badge
            label={`+${value.split(', ').length - 1}`}
            colour={colour}
            size="small"
          />
        </Tooltip>
      )}
    </Stack>
  ) : null;

export const getCrpColumns = (t: TType) => [
  {
    field: 'partOfCrpPlan',
    headerName: 'CRP',
    minWidth: 180,
    display: 'flex',
    aggregable: false,
    type: 'singleSelect',
    valueOptions: CRP_FY_RANGE_OPTIONS,
    filterOperators: [includesValueOperator, isAnyOfCrpOperator],
    cellClassName: 'crp-cell--overflow-visible',
    valueGetter: (_value, row) => {
      const crpItems = row?.partOfCrp || [];
      return crpItems.map((c) => getFyRange(c.fiscalYear)).join(', ');
    },
    renderCell: (params) =>
      crpBadgeRenderer(params.value, 'grey') || (
        <Badge label={t('common.notAdded')} colour="white" size="small" />
      ),
  },
  {
    field: 'partOfApprovedCRP',
    headerName: 'Approved CRP',
    minWidth: 180,
    display: 'flex',
    aggregable: false,
    type: 'singleSelect',
    valueOptions: CRP_FY_RANGE_OPTIONS,
    filterOperators: [includesValueOperator, isAnyOfCrpOperator],
    cellClassName: 'crp-cell--overflow-visible',
    valueGetter: (_value, row) => {
      const approved = row?.partOfCrp?.filter((c) => c.approved) || [];
      return approved.map((c) => getFyRange(c.fiscalYear)).join(', ');
    },
    renderCell: (params) =>
      crpBadgeRenderer(params.value, 'green') || (
        <Badge label={t('common.notAdded')} colour="white" size="small" />
      ),
  },
];

export const getTableColumns = (
  t: TType,
  countries: string[],
  directiveClickedEvent?: (id: string) => void,
) => [
  returnSingleSelectColumn(
    'projectStatus',
    t('ppm.columns.projectStatus'),
    getOptionsForTable(t, 'statuses', true),
    'statuses',
    t,
    true,
  ),
  textColumn('referenceId', t('ppm.columns.referenceId'), 200),
  textColumn('projectName', t('ppm.columns.projectName'), 300),
  returnSingleSelectColumn(
    'establishmentType',
    t('labels.establishmentType'),
    getOptionsForTable(t, 'establishmentTypes', true),
    'establishmentTypes',
    t,
  ),
  {
    field: 'countryName',
    headerName: t('ppm.columns.country'),
    aggregable: false,
    minWidth: 230,
    display: 'flex',
    type: 'singleSelect',
    valueOptions: countries.map((c) => ({
      value: c,
      label: c,
    })),
    renderCell: defaultRenderer,
  },
  textColumn('city', t('ppm.columns.city'), 100),
  textColumn('mtoolId', t('ppm.columns.mtoolId'), 150),
  {
    field: 'etrAndSynced',
    headerName: 'ETR Synced',
    width: 120,
    type: 'singleSelect',
    valueOptions: [
      { value: 'synced', label: 'YES' },
      { value: 'notSynced', label: 'NO' },
      { value: 'notApplicable', label: 'Not applicable' },
    ],
    groupable: false,
    valueGetter: (value, params) => {
      if (projectFromReltp(params?.projectSource)) {
        return params?.etrSynced ? 'synced' : 'notSynced';
      }
      return 'notApplicable';
    },
    groupingValueGetter: (value, params) => {
      if (projectFromReltp(params?.projectSource)) {
        return params?.etrSynced ? 'synced' : 'notSynced';
      }
      return 'notApplicable';
    },
  },
  getBooleanColumn('partOfOnePlan', t('ppm.columns.partOfOnePlan'), t),
  getBooleanColumn('realEstateSISAN', t('ppm.columns.realEstateSISAN'), t),
  {
    field: 'unitType',
    headerName: t('ppm.columns.unitType'),
    aggregable: false,
    minWidth: 230,
    display: 'flex',
    type: 'singleSelect',
    valueOptions: getOptionsForTable(t, 'unitTypes', true),
    valueGetter: (params) => getUnitTypeValue(params, t),
    groupingValueGetter: (params) => getUnitTypeValue(params, t),
    renderCell: defaultRenderer,
  },
  returnSingleSelectColumn(
    'transactionType',
    t('ppm.columns.transactionType'),
    getOptionsForTable(t, 'transactionTypes', true),
    'transactionTypes',
    t,
  ),
  {
    field: 'targetProjectStartDate',
    headerName: t('ppm.columns.startDate'),
    groupable: false,
    aggregable: false,
    minWidth: 150,
    display: 'flex',
    valueGetter: (value) => getFormattedDate(value),
    renderCell: defaultRenderer,
  },
  {
    field: 'targetProjectEndDate',
    headerName: t('ppm.columns.openingDate'),
    groupable: false,
    aggregable: false,
    minWidth: 150,
    display: 'flex',
    valueGetter: (value) => getFormattedDate(value),
    renderCell: defaultRenderer,
  },
  {
    field: 'projectEndDateFY',
    headerName: t('ppm.columns.projectEndDateFY'),
    groupable: true,
    aggregable: false,
    minWidth: 150,
    display: 'flex',
    valueGetter: (_value, row) =>
      row?.targetProjectEndDate
        ? getFY(0, new Date(row.targetProjectEndDate))
        : '',
    groupingValueGetter: (_value, row) =>
      row?.targetProjectEndDate
        ? getFY(0, new Date(row.targetProjectEndDate))
        : '',
    renderCell: defaultRenderer,
  },
  textColumn('businessUnitNumber', t('ppm.columns.buCode'), 100, true, false),
  textColumn('countryProjectResponsible', t('ppm.columns.responsible'), 200),
  textColumn('unifierId', 'Unifier ID', 200),
  textColumn('locationName', 'Location name', 200),
  {
    field: 'lastUpdatedDate',
    headerName: 'Last Updated Date',
    type: 'dateTime',
    groupable: false,
    aggregable: false,
    minWidth: 150,
    display: 'flex',
    valueGetter: (value) => (value ? new Date(value) : null),
    valueFormatter: (value) => (value ? getFormattedDate(value) : ''),
    renderCell: defaultRenderer,
  },
  textColumn('lastUpdatedUser', 'Last Updated User', 200),

  returnSingleSelectColumn(
    'unitCategory',
    'Unit category',
    getOptionsForTable(t, 'unitCategories', true),
    'unitCategories',
    t,
  ),
  returnSingleSelectColumn(
    'subPortfolio',
    t('ppm.columns.subPortfolio'),
    getOptionsForTable(t, 'ppm.subportfolios', true),
    'ppm.subportfolios',
    t,
  ),
  {
    field: 'solutionCategory',
    headerName: 'Solution category',
    aggregable: false,
    minWidth: 230,
    display: 'flex',
    type: 'singleSelect',
    valueOptions: getOptionsForTable(t, 'solutionCategories', true),
    valueGetter: (value) =>
      value ? getSolutionsLabelTextForGrid(value, t('solutionCategories')) : '',
    groupingValueGetter: (value) =>
      value ? t(`solutionCategories.${value}`) : '',
    renderCell: defaultRenderer,
  },
  {
    field: 'solutionType',
    headerName: 'Solution type',
    aggregable: false,
    minWidth: 230,
    display: 'flex',
    type: 'singleSelect',
    valueOptions: getOptionsForTable(t, 'solutionTypesGrid', true),
    valueGetter: (value) =>
      value
        ? getSolutionsLabelTextForGrid(value, {
            ...(t('solutionTypesGrid') as unknown as object),
            ...(t('solutionTypes') as unknown as object),
          })
        : '',
    groupingValueGetter: (value) =>
      value ? t(`solutionTypesGrid.${value}`) : '',
    renderCell: defaultRenderer,
  },
  textColumn('projectManagerLeader', 'Project manager/leader', 200),
  textColumn('projectAdmin', 'Project admin', 200),
  {
    field: 'fyBaselineAmount',
    headerName: 'FY27 Baseline amount K Euro',
    type: 'number',
    minWidth: 200,
    display: 'flex',
    groupable: false,
    aggregable: true,
    valueGetter: (_value, row) => {
      const baseline = row?.financialSummaryInfos?.find(
        (info) =>
          info.financialCategory === 'BASELINE' && info.fiscalYear === 'FY27',
      );
      return baseline?.totalInvestmentEur
        ? Math.round(baseline.totalInvestmentEur / 1000)
        : 0;
    },
    renderCell: (params: GridRenderCellParams) => (
      <Typography noWrap alignSelf={'anchor-center'}>
        {accounting.formatMoney(params.value, {
          precision: 0,
          symbol: EUR,
        })}
      </Typography>
    ),
  },
  {
    field: 'fy26BaselineAmount',
    headerName: 'FY26 Baseline amount K Euro',
    type: 'number',
    minWidth: 200,
    display: 'flex',
    groupable: false,
    aggregable: true,
    valueGetter: (_value, row) => {
      const baseline = row?.financialSummaryInfos?.find(
        (info) =>
          info.financialCategory === 'BASELINE' && info.fiscalYear === 'FY26',
      );
      return baseline?.totalInvestmentEur
        ? Math.round(baseline.totalInvestmentEur / 1000)
        : 0;
    },
    renderCell: (params: GridRenderCellParams) => (
      <Typography noWrap alignSelf={'anchor-center'}>
        {accounting.formatMoney(params.value, {
          precision: 0,
          symbol: EUR,
        })}
      </Typography>
    ),
  },
  ...getCrpColumns(t),
  {
    field: 'isep',
    headerName: t('ppm.columns.isep'),
    minWidth: 100,
    display: 'flex',
    aggregable: false,
    type: 'singleSelect',
    valueOptions: booleanSelectOptions,
    renderCell: (params) =>
      params.value ? (
        <Badge label={t('common.yes')} colour="green" size="small" />
      ) : (
        <Badge label={t('common.no')} colour="white" size="small" />
      ),
  },
  returnSingleSelectColumn(
    'isepStatus',
    'ISEP Status',
    getOptionsForTable(t, 'ppm.isepStatuses', true),
    'ppm.isepStatuses',
    t,
    true,
  ),

  {
    field: 'projectClosingDate',
    headerName: 'Project closing date',
    groupable: false,
    aggregable: false,
    minWidth: 150,
    display: 'flex',
    valueGetter: (value) => getFormattedDate(value),
    renderCell: defaultRenderer,
  },
  {
    field: 'ffcDesignStartDate',
    headerName: 'CFF design start date',
    groupable: false,
    aggregable: false,
    minWidth: 150,
    display: 'flex',
    valueGetter: (value) => getFormattedDate(value),
    renderCell: defaultRenderer,
  },
  getBooleanColumn('partOfApprovedProgramme', 'Part of approved programme', t),
  returnSingleSelectColumn(
    'etrProjectPrioritization',
    'Project prioritisation',
    getOptionsForTable(t, 'etrProjectPrioritization', true),
    'etrProjectPrioritization',
    t,
  ),
  textColumn('state', 'State', 150),
  textColumn('address', 'Address', 250),
  returnSingleSelectColumn(
    'siteType',
    'Site type',
    getOptionsForTable(t, 'siteTypes', true),
    'siteTypes',
    t,
  ),
  textColumn('businessUnitType', 'BU Type', 150),
  textColumn('projectOwner', 'Project owner', 200),
  textColumn('projectOrderer', 'Project orderer', 200),
  getBooleanColumn(
    'isLeasedFromAnotherIngkaCompany',
    'Leased from another Ingka company',
    t,
  ),
  {
    field: 'solutionPurpose',
    headerName: 'Solution purpose',
    minWidth: 200,
    type: 'singleSelect',
    valueOptions: getOptionsForTable(t, 'solutionPuposes', true),
    filterOperators: [includesValueOperator],
    valueGetter: (value) =>
      value
        ?.split(', ')
        .map((v) =>
          getTranslatedValue(SolutionPuposesEnum[v] || v, 'solutionPuposes', t),
        )
        .join(', '),
    groupingValueGetter: (value) =>
      value
        ?.split(', ')
        .map((v) =>
          getTranslatedValue(SolutionPuposesEnum[v] || v, 'solutionPuposes', t),
        )
        .join(', '),
    renderCell: defaultRenderer,
  },
  returnSingleSelectColumn(
    'fundedBy',
    'Funded by',
    getOptionsForTable(t, 'ppm.fundedBy.options', true),
    'ppm.fundedBy.options',
    t,
  ),
  returnSingleSelectColumn(
    'ocnPriority',
    'OCN Priority',
    getOptionsForTable(t, 'ocnPriority', true),
    'ocnPriority',
    t,
  ),

  {
    field: 'isepDate',
    headerName: 'ISEP Date',
    groupable: false,
    aggregable: false,
    minWidth: 150,
    display: 'flex',
    valueGetter: (value) => getFormattedDate(value),
    renderCell: defaultRenderer,
  },
  textColumn('etrUnitId', t('projects.properties.etrUnitId'), 200),
  ...getFinancialColumns(t),
  ...getNextForecastColumns(t),
  ...getFutureFyForecastColumns(t),
  {
    field: 'confidential',
    headerName: 'Confidentiality',
    type: 'singleSelect',
    valueOptions: getOptionsForTable(t, 'confidentialityLevels', true),
    aggregable: false,
    minWidth: 180,
    display: 'flex',
    valueGetter: (value) =>
      getTranslatedValue(value, 'confidentialityLevels', t),
    groupingValueGetter: (value) =>
      getTranslatedValue(value, 'confidentialityLevels', t),
    renderCell: (params) =>
      params.row?.confidential ? (
        <Badge
          label={params.value}
          colour={confidentialityColourMap[params.row.confidential] || 'grey'}
          size="small"
        />
      ) : null,
  },
  {
    field: FY_BUDGET_FILTER_FIELD,
    headerName: 'FY Budget FYs',
    filterable: true,
    sortable: false,
    groupable: false,
    aggregable: false,
    filterOperators: [fyBudgetFilterOperator],
  } as GridColDef,
  {
    field: 'isDirectiveComplete',
    headerName: 'Project Directive',
    minWidth: 200,
    display: 'flex',
    aggregable: false,
    type: 'singleSelect',
    cellClassName: 'directive-cell--overflow-visible',
    valueOptions: [
      { value: true, label: 'Completed' },
      { value: false, label: 'Not completed' },
    ],
    groupingValueGetter: (value) => {
      return value ? 'Completed' : 'Not completed';
    },
    renderCell: (params: GridRenderCellParams) => {
      if (params.value === null || params.value === undefined)
        return (
          <Typography noWrap style={{ margin: 'auto' }}>
            -
          </Typography>
        );
      const label = params.value ? 'Completed' : 'Not completed';
      if (params.rowNode?.type === 'group') {
        return <Typography noWrap>{label}</Typography>;
      }
      return (
        <Tooltip
          tooltipText="View project directive"
          describedById="directive-tooltip"
          position="bottom"
        >
          <Button
            type="tertiary"
            size="xsmall"
            ssrIcon={params.value ? documentCheckmark : document}
            iconPosition="leading"
            text={label}
            onClick={(e) => {
              e.stopPropagation();
              directiveClickedEvent?.(params.row.referenceId);
            }}
          />
        </Tooltip>
      );
    },
  },
];

export const DEFAULT_VIEW_COLUMNS = [
  '__check__',
  'projectStatus',
  'referenceId',
  'projectName',
  'establishmentType',
  'unitType',
  'targetProjectStartDate',
  'targetProjectEndDate',
  'partOfCrpPlan',
  'partOfApprovedCRP',
  'isep',
  'confidential',
  'isDirectiveComplete',
  'subPortfolio',
];

export const buildDefaultVisibility = (
  columns: { field: string }[],
  visibleColumns: string[],
) => {
  const visibility: Record<string, boolean> = {};
  columns.forEach((col) => {
    visibility[col.field] = visibleColumns.includes(col.field);
  });
  return visibility;
};

export const FINANCIAL_VIEW_COLUMNS = [
  '__check__',
  'projectStatus',
  'id',
  'projectName',
  'confidential',
  'dpProgress',
  ...FINANCIAL_COLUMNS,
];

export const FORECAST_VIEW_COLUMNS = [
  '__check__',
  'id',
  'projectName',
  'dpProgress',
  'isepDate',
  'totalForecast',
  'totalReleased',
  'totalActuals',
  ...NextFyForecast,
];

export const calculateStats = (projects, currencySymbol) => {
  const stats = projects?.reduce((acc, { stats = emptyStats() } = {}) => {
    return {
      opex: acc.opex + stats.opex,
      opexEur: acc.opexEur + stats.opexEur,
      capex: acc.capex + stats.capex,
      capexEur: acc.capexEur + stats.capexEur,
      total: acc.total + stats.total,
      totalEur: acc.totalEur + stats.totalEur,
    };
  }, emptyStats());
  return {
    opex: accounting.formatMoney(stats.opex / 1000, {
      precision: 0,
      symbol: `K${currencySymbol}`,
    }),
    opexEur: accounting.formatMoney(stats.opexEur / 1000, {
      precision: 0,
      symbol: `K${EUR}`,
    }),

    capex: accounting.formatMoney(stats.capex / 1000, {
      precision: 0,
      symbol: `K${currencySymbol}`,
    }),
    capexEur: accounting.formatMoney(stats.capexEur / 1000, {
      precision: 0,
      symbol: `K${EUR}`,
    }),
    total: accounting.formatMoney(stats.total / 1000, {
      precision: 0,
      symbol: `K${currencySymbol}`,
    }),
    totalEur: accounting.formatMoney(stats.totalEur / 1000, {
      precision: 0,
      symbol: `K${EUR}`,
    }),
  };
};

export const returnStatistics = (stats, oneCountrySelected, projectsNumber) => [
  {
    label: 'Opex',
    helperText: oneCountrySelected ? stats.opexEur : '',
    value: oneCountrySelected ? stats.opex : stats.opexEur,
  },
  {
    label: 'Capex',
    helperText: oneCountrySelected ? stats.capexEur : '',
    value: oneCountrySelected ? stats.capex : stats.capexEur,
  },
  {
    label: 'Total investment cost',
    helperText: oneCountrySelected ? stats.totalEur : '',
    value: oneCountrySelected ? stats.total : stats.totalEur,
  },
  {
    label: 'Total Projects',
    value: projectsNumber.toString(),
  },
];
